<script>
  import { createEventDispatcher, onMount } from 'svelte';

  import Input from '@components/Input.svelte';
  import Button from '@components/Button.svelte';
  import Auth from '@utils/auth';
  import Notification from '@utils/notification';
  import BrowserUtils from '@utils/browser-utils';
  import Log from '@utils/log';
  import UserAjax from '@ajax/user';
  import OAuthAjax from '@ajax/oauth';
  import AppInviteAjax from '@ajax/app_invite';
  import TermsAndPrivacyAjax from '@ajax/terms_and_privacy';

  import EmailInput from '@app-components/EmailInput.svelte';
  import PasswordInput from '@app-components/PasswordInput.svelte';
  import Cookie from 'js-cookie';
  import { goto } from '$app/navigation';
  import jQuery from 'jquery';
  import { getCurrentUserStore } from '@stores/current_user';
  import { browser } from '$app/environment';
  import queryString from 'query-string';

  const dispatch = createEventDispatcher();

  export let mobile = false;
  export let successCallback;
  export let afterLoginCallback;
  export let showPassword = true;
  export let signInView = false;
  export let showActionLink = true;
  export let namePlaceholder = 'Full name';
  export let header;
  export let showOAuthButtons = true;
  export let inviteFlow = false;
  export let closeModal;
  export let saveButtonFlow = false;
  export let customButtonSignupText;
  export let prefilledName;
  export let prefilledEmail;

  let emailValid = typeof location !== 'undefined' && queryString.parse(location.search).email ? true : false;
  let noAccountError = false;
  let currentUser = getCurrentUserStore('default');

  $: signInFormValid = email?.length && emailValid && signInPassword?.length;
  $: createFormValid = email?.length && emailValid && (showPassword === false || password?.length) && name?.length;
  $: formValid = signInView ? signInFormValid : createFormValid;

  export let emailValidationFn = async (_emailValue) => {};

  const validateAndCheckIfEmailTaken = async (emailValue) => {
    const error = await emailValidationFn(emailValue);

    if (error) return error;

    // only check for email re-use if the email in the input field matches the
    // email regex / looks like a valid email address.
    let exists = false;
    if (BrowserUtils.emailIsValid(emailValue)) {
      exists = await UserAjax.checkEmail(emailValue);
    }

    return exists ? 'exists' : undefined;
  };

  let defaultSignInView = signInView;

  export let toggleActionLinkView = () => {
    noAccountError = false;
    signInView = !signInView;
  };

  export let beforeDataSend;

  let termsAndPrivacyConfig = { terms_url: '', privacy_url: '' };

  let saving = false;
  let name = (typeof location !== 'undefined' && queryString.parse(location.search).name) || prefilledName || '';
  let email = (typeof location !== 'undefined' && queryString.parse(location.search).email) || prefilledEmail || '';
  let password;
  let signInPassword;

  let formContainerEl;

  let showSpinner = false;

  $: headerText = header ? header : signInView ? 'Login' : 'Create account';
  $: buttonText = signInView ? 'Sign in' : customButtonSignupText || 'Create account';

  let submitOnEnter = function (e) {
    if (e.detail.which == 13) {
      if (signInView) {
        signIn();
      } else {
        saveAccount();
      }
    }
  };

  let close = function () {
    saving = false;
    signInView = defaultSignInView;

    name = '';
    email = '';
    signInPassword = '';

    closeModal();
  };

  let signIn = async function () {
    saving = true;

    let emailFromInput = jQuery('#login-email-field input').val();
    let passFromInput = jQuery('#login-password-field input').val();

    try {
      let resp = await Auth.signIn(emailFromInput, passFromInput);
      Log.track(`[SIGN IN] [1] (${emailFromInput}) | resp: ${JSON.stringify(resp)}`);

      let user = await Auth.load(currentUser, resp.token);
      Log.track(`[SIGN IN] [2] (${emailFromInput}) | user: ${user && user.id}`);

      Cookie.set('fs-rec', undefined);

      try {
        afterLoginCallback && (await afterLoginCallback(user));
        await successCallback(user, true);
      } catch (err) {
        Log.track(`[SIGN IN] [3] (${emailFromInput}) | successCallback error: ${JSON.stringify(err)}`);
      }

      close();
    } catch (err) {
      if (err) {
        Notification.send({
          text: "No account found or invalid password. <a href='/forgot_password' style='margin-left: 10px; color: #cabefb;'>Forgot password?</a>",
        });
        noAccountError = true;
      }
    }

    saving = false;
  };

  let getSaveData = function () {
    if (!showPassword) {
      password = Math.random().toString(36).slice(-8);
    }

    let data = {
      user: {
        name: name,
        email: email,
        password: password,
        password_confirmation: password,
        source: Cookie.get('utm_source'),
        utm_medium: Cookie.get('utm_medium'),
        utm_campaign: Cookie.get('utm_campaign'),
        utm_content: Cookie.get('utm_content'),
        external_referrer: Cookie.get('external_referrer'),
      },
    };

    data = beforeDataSend(data);

    return data;
  };

  let saveInvitePendingUserAccount = async function () {
    saving = true;
    let data = getSaveData();

    try {
      let resp = await AppInviteAjax.createPendingUser(data);

      if (resp && resp.code) {
        dispatch('go-to-username-step', resp);
      }
    } catch (err) {
      if (err && err.full_errors && err.full_errors.length > 0) {
        Notification.send({
          text: err.full_errors[0],
        });
      }

      saving = false;
    }
  };

  let saveAccount = async function () {
    if (saving) {
      return;
    }

    if (inviteFlow) {
      saveInvitePendingUserAccount();
      return;
    }

    saving = true;
    let data = getSaveData();

    try {
      let resp = await UserAjax.create(data);

      if (currentUser) {
        await Auth.signout(currentUser, { redirectToRoot: false });
      }

      let user = await Auth.load(currentUser, resp.token);

      if (saveButtonFlow) {
        dispatch('go-to-username-step', resp);
        return;
      }

      Cookie.set('fs-rec', undefined);

      try {
        await successCallback(user);
      } catch {}

      close();
    } catch (err) {
      if (err && err.full_errors && err.full_errors.length > 0) {
        Notification.send({
          text: err.full_errors[0],
        });
      }
    }

    saving = false;
  };

  let handleOauthResponseWithInviteFlow = function (resp) {
    dispatch('go-to-username-step', resp);
  };

  let onGoogleSignIn = async function (response) {
    let token = response.credential;
    try {
      showSpinner = true;

      let resp = await OAuthAjax.signInGoogle(
        beforeDataSend({
          token: token,
          invite_flow: inviteFlow,
        })
      );

      if (resp.type === 'user') {
        let user = await Auth.load(currentUser, resp.token);
        Cookie.set('fs-rec', undefined);
        afterLoginCallback && (await afterLoginCallback(user));
        successCallback(user);
        close();
      } else {
        handleOauthResponseWithInviteFlow(resp);
      }
    } catch (err) {
      if (err && err.error) {
        Notification.send({
          text: err.error,
        });
      }

      showSpinner = false;
    }
  };

  // chrome on ios doesn't work with autofill password without this fix
  let floatingContainerTimeout;
  let shouldShowFloatingContainer = false;

  let showFloatingContainerPreventScrolling = function (e) {
    e.preventDefault();
  };

  let showFloatingContainer = function () {
    if (shouldShowFloatingContainer) {
      let formContainer = jQuery(formContainerEl);
      clearTimeout(floatingContainerTimeout);
      jQuery(formContainer).addClass('floating-login-container');

      if (window.visualViewport) {
        setTimeout(() => {
          jQuery(formContainer).css('height', window.visualViewport.height);
        }, 100);
      }

      document.body.addEventListener('touchmove', showFloatingContainerPreventScrolling, {
        passive: false,
      });
    }
  };

  let hideFloatingContainer = function () {
    if (shouldShowFloatingContainer) {
      clearTimeout(floatingContainerTimeout);

      floatingContainerTimeout = setTimeout(() => {
        jQuery(formContainerEl).removeClass('floating-login-container').css('height', 'unset');

        document.body.removeEventListener('touchmove', showFloatingContainerPreventScrolling);
      }, 100);
    }
  };

  let triggerMetaPixelTrackInitiateCheckout = () => {
    if (typeof window !== 'undefined' && window.fbq !== undefined) {
      window.fbq('track', 'InitiateCheckout');
    }
  };

  (async () => {
    try {
      termsAndPrivacyConfig = await TermsAndPrivacyAjax.get();
    } catch {
      Log.info('Unable to load privacy & terms in frontend (in create account)', true);
    }
  })();

  onMount(() => {
    let checkGoogleApi = setInterval(() => {
      if (typeof google !== 'undefined' && google.accounts && google.accounts.id) {
        clearInterval(checkGoogleApi);

        google.accounts.id.initialize({
          client_id: window.GOOGLE_CLIENT_ID,
          callback: onGoogleSignIn,
        });
        google.accounts.id.renderButton(document.getElementById('google-sign-in-btn'), {
          size: 'medium',
          width: mobile ? window.innerWidth - 100 : 350,
          shape: 'pill',
        });
      }
    }, 100);

    triggerMetaPixelTrackInitiateCheckout();
  });
</script>

<style style="text/scss" lang="scss">
  @import '../../../vendor/design-system.scss';
  * :global(.abcRioButton) {
    width: 100% !important;
    box-shadow: unset !important;
    border: 0px !important;
    border-radius: 12px !important;
  }

  * :global(.abcRioButtonContents) {
    font-weight: bold !important;
    font-size: 15px !important;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif,
      'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji' !important;
    color: rgba(0, 0, 0, 0.8) !important;
    margin-left: 7px !important;
  }

  * :global(.abcRioButtonContentWrapper) {
    display: flex !important;
    justify-content: center !important;
    margin-left: -9px !important;
  }

  #google-sign-in-btn {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .sign-in-and-inputs-container {
    display: flex;
    flex-direction: column;
  }

  .left-right-container {
    display: flex;
    margin-right: -30px;

    .left-side {
      margin-left: -30px;
      margin-top: -50px;
      margin-bottom: -50px;
    }

    .right-side {
      flex: 1;
      flex-shrink: 0;
      justify-content: center;
      padding-left: 40px;
      padding-right: 40px;
      overflow: auto;
      margin-bottom: -30px;
      height: calc(100vh - 140px);
      overflow: auto;

      .header-1 {
        font-size: 24px;
        line-height: 32px;
        font-weight: bold;
        margin-top: 8px;
      }

      .sign-in-with {
        margin-top: 60px;

        .sign-in-btn {
          border: 1px solid #ececec;
          border-radius: 12px;
          height: 48px;
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;
          transition: 0.2s all;

          &:hover {
            border: 1px solid #7054dc;
          }
        }
      }

      .or-svg {
        margin-top: 32px;
        margin-bottom: 24px;
      }
    }

    .terms-and-privacy {
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      color: #5e518e;
      margin-top: 24px;
      margin-bottom: 24px;

      a {
        color: #a893f9;
        font-weight: 700;

        &:hover {
          color: #7054dc;
        }
      }
    }
  }

  .ui-actions {
    margin-top: 24px;
    margin-left: auto;
    margin-right: auto;
    width: 100%;
  }

  .input-label {
    text-align: left;
    margin-bottom: 5px;
    font-size: 12px;
    line-height: 20px;
  }

  .action-link-container {
    margin-top: 15px;
    margin-bottom: 15px;
    text-align: center;
    &.error {
      .top-text {
        color: $red;
      }

      .action-link-bottom.top-link {
        color: $red;
        font-weight: bold;
        &:hover {
          color: $red-dark;
        }
      }
    }
    .top-text {
      font-size: 14px;
      line-height: 20px;
      color: $purple-70;
    }

    .action-link-bottom {
      margin-left: 6px;
      color: #a893f9;
      font-weight: bold;
      font-size: 14px;
      line-height: 20px;
      cursor: pointer;

      &:hover {
        color: #7054dc;
      }
    }
  }

  .mobile {
    margin-right: unset !important;
    min-height: 400px;

    .or-svg {
      svg {
        width: 100% !important;
      }
    }

    .right-side {
      padding-left: 10px !important;
      padding-right: 10px !important;
      overflow: hidden !important;
      height: unset !important;
    }
  }

  :global(.floating-login-container) {
    position: fixed;
    background: white;
    padding: 20px;
    padding-left: 40px;
    padding-right: 40px;
    top: 0px;
    right: 0px;
    left: 0px;
    z-index: 999;
  }
</style>

<svelte:head>
  {#if browser}
    <script src="https://accounts.google.com/gsi/client" async defer></script>
  {/if}
</svelte:head>

<div class="left-right-container" class:mobile={mobile}>
  {#if !mobile}
    <div class="left-side">
      <img
        src="/images/login-create-left.png"
        style="width: 654px; height: 862px; object-fit: cover; margin-top: -2px; object-position: left;" />
    </div>
  {/if}
  <div class="right-side purple-scrollbar" style={mobile ? '' : 'position: relative'}>
    <div>
      <img src="/images/create-login-modal-right-side.svg" />
    </div>
    <div class="header-1">{@html headerText}</div>

    {#if showSpinner}
      <div style="position: absolute; left: calc(50% - 16px); top: calc(50% - 16px);">
        <i class="fa fa-spinner fa-spin" style="font-size: 32px; color: #7054dc;" />
      </div>
    {:else}
      <div class="sign-in-and-inputs-container">
        {#if showOAuthButtons}
          <div class="sign-in-with">
            <div id="google-sign-in-btn" />
          </div>

          <div class="or-svg">
            <svg width="360" height="17" viewBox="0 0 360 17" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                opacity="0.5"
                d="M175.638 9.64771C175.638 9.24038 175.69 8.88071 175.794 8.56871C175.907 8.24804 176.058 7.97938 176.249 7.76271C176.44 7.54604 176.661 7.38138 176.912 7.26871C177.172 7.15604 177.445 7.09971 177.731 7.09971C178.017 7.09971 178.286 7.15604 178.537 7.26871C178.797 7.38138 179.022 7.54604 179.213 7.76271C179.404 7.97938 179.551 8.24804 179.655 8.56871C179.768 8.88071 179.824 9.24038 179.824 9.64771C179.824 10.055 179.768 10.419 179.655 10.7397C179.551 11.0517 179.404 11.316 179.213 11.5327C179.022 11.7407 178.797 11.901 178.537 12.0137C178.286 12.1264 178.017 12.1827 177.731 12.1827C177.445 12.1827 177.172 12.1264 176.912 12.0137C176.661 11.901 176.44 11.7407 176.249 11.5327C176.058 11.316 175.907 11.0517 175.794 10.7397C175.69 10.419 175.638 10.055 175.638 9.64771ZM174.468 9.64771C174.468 10.1417 174.537 10.601 174.676 11.0257C174.815 11.4504 175.023 11.823 175.3 12.1437C175.577 12.4557 175.92 12.7027 176.327 12.8847C176.734 13.058 177.202 13.1447 177.731 13.1447C178.268 13.1447 178.736 13.058 179.135 12.8847C179.542 12.7027 179.885 12.4557 180.162 12.1437C180.439 11.823 180.647 11.4504 180.786 11.0257C180.925 10.601 180.994 10.1417 180.994 9.64771C180.994 9.15371 180.925 8.69438 180.786 8.26971C180.647 7.83638 180.439 7.46371 180.162 7.15171C179.885 6.83104 179.542 6.57971 179.135 6.39771C178.736 6.21571 178.268 6.12471 177.731 6.12471C177.202 6.12471 176.734 6.21571 176.327 6.39771C175.92 6.57971 175.577 6.83104 175.3 7.15171C175.023 7.46371 174.815 7.83638 174.676 8.26971C174.537 8.69438 174.468 9.15371 174.468 9.64771ZM182.258 6.28071V13.0017H183.363V10.0117C183.363 9.57838 183.406 9.19704 183.493 8.86771C183.58 8.52971 183.718 8.24371 183.909 8.00971C184.1 7.77571 184.351 7.59804 184.663 7.47671C184.975 7.35538 185.352 7.29471 185.794 7.29471V6.12471C185.196 6.10738 184.702 6.22871 184.312 6.48871C183.922 6.74871 183.593 7.15171 183.324 7.69771H183.298V6.28071H182.258Z"
                fill="black" />
              <line x1="155" y1="8.50171" x2="4.37114e-08" y2="8.50172" stroke="#ECECEC" />
              <line x1="360" y1="8.50171" x2="205" y2="8.50172" stroke="#ECECEC" />
            </svg>
          </div>
        {:else}
          <div style="height: 40px;">&nbsp;</div>
        {/if}

        <div class="form-container" bind:this={formContainerEl}>
          {#if signInView}
            <div id="login-email-field">
              <div class="input-label">Email</div>
              <EmailInput
                name="email"
                on:keyup={submitOnEnter}
                bind:value={email}
                focus={!mobile}
                on:focus={showFloatingContainer}
                on:blur={hideFloatingContainer}
                inputProps={{ autocomplete: 'username' }}
                validateFn={emailValidationFn}
                validateAfterTyping={true}
                bind:valid={emailValid}
                inputStyle="font-weight: 400"
                style="width: 275px; margin-left: auto; margin-right: auto;"
                placeholder="Your email address" />
            </div>

            <div style="margin-top: 12px;" id="login-password-field">
              <div class="input-label">Password</div>
              <PasswordInput
                name="password"
                on:focus={showFloatingContainer}
                on:blur={hideFloatingContainer}
                on:keyup={submitOnEnter}
                bind:value={signInPassword}
                inputProps={{ autocomplete: 'current-password' }}
                inputStyle="font-weight: 400"
                style="width: 275px; margin-left: auto; margin-right: auto;" />
            </div>
          {:else}
            <div>
              <div class="input-label">
                {namePlaceholder}
              </div>
              <Input
                focus={!mobile}
                bind:value={name}
                inputStyle="font-weight: 400"
                style="width: 275px; margin-left: auto; margin-right: auto;" />
            </div>

            <div style="margin-top: 12px;">
              <div class="input-label">Email address</div>
              <EmailInput
                bind:value={email}
                validateFn={validateAndCheckIfEmailTaken}
                validateAfterTyping={true}
                bind:valid={emailValid}
                inputStyle="font-weight: 400"
                style="width: 275px; margin-left: auto; margin-right: auto;"
                on:keyup={submitOnEnter}
                on:sign-in-clicked={() => {
                  signInView = true;
                }} />
            </div>

            {#if showPassword}
              <div style="margin-top: 12px;">
                <div class="input-label">Password</div>
                <PasswordInput
                  bind:value={password}
                  inputStyle="font-weight: 400"
                  style="width: 275px; margin-left: auto; margin-right: auto;"
                  on:keyup={submitOnEnter} />
              </div>
            {/if}
          {/if}

          <div class="ui-actions">
            <Button
              colorScheme="purple"
              shadow={false}
              on:click={signInView ? signIn : saveAccount}
              disabled={signInView ? false : !formValid}>
              {#if saving}
                <div>
                  <i class="fa fa-spin fa-spinner" />
                  <span style="margin-left: 10px;">{signInView ? 'Working' : 'Saving'}</span>
                </div>
              {:else}{buttonText}{/if}
            </Button>
          </div>
        </div>
      </div>

      {#if signInView == false}
        <div class="terms-and-privacy">
          By continuing, you agree to Benable's <a
            on:click={() => window.open(termsAndPrivacyConfig.terms_url)}
            href={termsAndPrivacyConfig.terms_url}
            target="_blank">Terms of Service</a>
          and acknowledge you've read our
          <a
            on:click={() => window.open(termsAndPrivacyConfig.privacy_url)}
            href={termsAndPrivacyConfig.privacy_url}
            target="_blank">Privacy Policy</a
          >.
        </div>
      {/if}

      {#if showActionLink}
        <div class="action-link-container {noAccountError ? 'error' : ''}">
          <span class="top-text">{signInView ? 'Need an account?' : 'Already have an account?'}</span>
          <span class="action-link-bottom top-link" on:click={toggleActionLinkView}
            >{signInView ? 'Create account' : 'Login'}</span>

          {#if signInView}
            <div style="margin-top: 20px;">
              <a on:click={() => goto('/forgot_password')} href="/forgot_password" class="action-link-bottom"
                >Forgot password</a>
            </div>
          {/if}
        </div>
      {/if}
    {/if}
  </div>
</div>
